import React, { useEffect } from "react";
import PageContainer from "../../../components/PageContainer/index";
import LoadBoundary from "../../../components/LoadBoundary";
import LoadingMessage from "../../../components/LoadingMessage";
import { useDispatch } from "react-redux";
import useUser from "../../../hooks/useUser";
import usePush from "../../../hooks/usePush";
import useShortCode from "../../../hooks/useShortCode";
import AccountService from "../../../services/account-service";
import { setUser } from "../../login/duck/actions/login";
import useOrganisation from "../../../hooks/useOrganisation";
import CashFlowForecastTease from "../components/CashFlowForecastTease.jsx";
import RemoteUnavailable from "../../../components/RemoteUnavailable";
import useModuleSubscription from "../../../hooks/useModuleSubscription";
import InsufficientPermissions from "../components/InsufficientPermissions.jsx";

const CashflowUnavailable = () => <RemoteUnavailable errorCode="CF-503" />;

const CashflowAppLazy = React.lazy(() =>
  import("cashflow/App").catch((error) => ({ default: CashflowUnavailable })),
);

const CashflowForecastingRemoteRoute = () => {
  const sc = useShortCode();

  return <CashflowAppLazy key={sc || "no-org"} organisation={sc} />;
};

const CashFlowForecastGate = () => {
  const dispatch = useDispatch();
  const { hasModule: hasCashflowModule, loading: moduleLoading } =
    useModuleSubscription("cashflow-module");
  const { token } = useUser();
  const {
    cashFlowIntelligence,
    subscription: { isInTrial: isChaserTrial } = {},
  } = useOrganisation();
  const { execute: executeGetCurrentUser } = usePush(
    AccountService.getCurrentUser,
    {
      suspense: false,
    },
  );

  useEffect(() => {
    const updateUser = async () => {
      const [err, updatedUser] = await executeGetCurrentUser();
      if (!err && updatedUser) {
        dispatch(setUser({ ...updatedUser, token }));
      }
    };

    updateUser();
  }, [dispatch, executeGetCurrentUser, token]);

  if (moduleLoading) return <LoadingMessage center />;

  if (hasCashflowModule) return <CashflowForecastingRemoteRoute />;

  const trialEndDate = cashFlowIntelligence?.cashFlowForecastTrialEndDate;
  const isTrialActive = trialEndDate && new Date(trialEndDate) > new Date();

  if (isTrialActive) return <CashflowForecastingRemoteRoute />;

  if (!isTrialActive) {
    return (
      <CashFlowForecastTease
        isTrialExpired={trialEndDate && !isTrialActive}
        isChaserTrial={isChaserTrial}
      />
    );
  }

  // Important comment to keep to understand the logic of having the component in the end of the function opened!
  // No trial started yet, CF Forecasting is available for all users to start trial, but only subscribers can activate add-on after trial expiry
  if (!hasCashflowModule) {
    return (
      <InsufficientPermissions message="Cash Flow module is not active for this subscription. Please update your subscription to access this feature." />
    );
  }

  return <CashflowForecastingRemoteRoute />;
};

const CashflowForecastingTable = () => {
  return (
    <PageContainer maxWidth="initial" padding="8px 16px !important">
      <LoadBoundary>
        <CashFlowForecastGate />
      </LoadBoundary>
    </PageContainer>
  );
};

export default CashflowForecastingTable;
